import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import EmptyState from "@/components/base/EmptyState";
import { CATEGORY_TYPES, SORT_LABELS, TYPE_LABELS, sortMovies, filterMovies, type SortKey } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";

const sortOptions: SortKey[] = ["latest", "popular", "rating"];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const activeType = params.get("type") ?? "";
  const sort = (params.get("sort") as SortKey) ?? "latest";

  const [input, setInput] = useState(keyword);

  useSeo({
    title: keyword ? `“${keyword}” 搜索结果 - 91影视` : "影视搜索 - 91影视",
    description: "在91影视搜索影视作品，支持片名、演员、导演关键词模糊搜索与类型过滤。",
    keywords: "91影视,影视搜索,电影搜索,电视剧搜索",
  });

  const result = useMemo(() => {
    const filtered = filterMovies({ keyword, type: activeType || undefined });
    return sortMovies(filtered, sort);
  }, [keyword, activeType, sort]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateParam("q", input.trim());
  };

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900">搜索结果</span>
      </nav>

      <header className="mt-4">
        <h1 className="text-2xl font-extrabold text-foreground-950 md:text-3xl">影视搜索</h1>
        <p className="mt-2 text-sm text-foreground-500">
          {keyword ? (
            <>
              当前关键词：<span className="font-semibold text-primary-600">{keyword}</span>
            </>
          ) : (
            "输入片名、演员或导演，找到你想要的影视作品。"
          )}
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1.5"
      >
        <i className="ri-search-line ml-2 text-lg text-foreground-400"></i>
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="输入片名 / 演员 / 导演"
          className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
        />
        <button
          type="submit"
          className="shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          搜索
        </button>
      </form>

      <div className="mt-6 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">结果类型：</span>
          <button
            type="button"
            onClick={() => updateParam("type", "")}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              !activeType ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            全部
          </button>
          {CATEGORY_TYPES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => updateParam("type", item)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                activeType === item
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {TYPE_LABELS[item]}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">排序：</span>
          {sortOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => updateParam("sort", option)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                sort === option
                  ? "bg-secondary-500 text-foreground-950"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {SORT_LABELS[option]}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-5 text-sm text-foreground-500">
        共找到 <span className="font-semibold text-primary-600">{result.length}</span> 条结果
      </p>

      <div className="mt-4">
        {result.length > 0 ? (
          <MovieGrid movies={result} />
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="没有找到相关影视作品"
            description="换个关键词试试，或浏览我们的热门推荐内容。"
            actionLabel="浏览热门影视"
            to="/category/movie"
          />
        )}
      </div>
    </div>
  );
}